/**
 * Generic pricing calculator with rule-based pricing support
 * Handles complex conditional pricing formulas and bulk aggregation
 */

import { roundTo } from '../../utils/geometry/epsilon';
import { DEFAULT_PRICES, DEFAULT_PROFITS } from './pricingConfig';
// import * as Sentry from '@sentry/react';
import { DEFAULT_RULES } from './PricingRules';
import createSafeEvaluator from '../../utils/eval/createSafeEvaluator';
import createSafeConditionEvaluator from '../../utils/eval/createSafeConditionEvaluator';

export class PricingCalculator {
  /**
   * Initialize pricing calculator with rules, default prices, and profits
   * @param {object} pricingRules - Rules defining conditional pricing logic
   * @param {object} defaultPrices - Default prices for all component types
   * @param {object} defaultProfits - Default profit margins for all component types
   */
  constructor({
    ruleConfig = DEFAULT_RULES,
    priceConfig = DEFAULT_PRICES,
    profitConfig = DEFAULT_PROFITS,
  }) {
    this.pricingRules = ruleConfig;
    this.defaultPrices = priceConfig;
    this.defaultProfits = profitConfig;
    this.variables = { ...(ruleConfig?.variables ?? {}) };
    this.cache = new Map();
  }

  /**
   * Get cache performance statistics
   * @returns {object} Cache size and list of cached keys
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
    };
  }

  /**
   * Clear all cached calculation results
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Generate unique cache key for component calculation
   * @param {string} type - Component type
   * @param {string} category - Component category
   * @param {string} subtype - Optional subtype
   * @param {number} quantity - Component quantity
   * @param {number} unitPrice - Unit price
   * @returns {string} Cache key
   */
  getCacheKey(type, category, subtype, quantity, unitPrice) {
    return `${category}:${type}:${subtype}:${quantity}:${unitPrice}`;
  }

  /**
   * Get nested value from config object supporting subtypes
   * Handles both simple values and nested object structures
   * @param {object} obj - Config object (prices or profits)
   * @param {string} category - Main category
   * @param {string} type - Component type
   * @param {string} subtype - Optional subtype for nested lookup
   * @returns {number} Value from config (0 if not found)
   */
  getNestedValue(obj, category, type, subtype = null) {
    const typeValue = obj[category]?.[type];
    if (!typeValue) return 0;
    if (
      subtype &&
      typeof typeValue === 'object' &&
      typeof typeValue !== 'number'
    ) {
      return typeValue[subtype] || 0;
    }
    if (typeof typeValue === 'number') return typeValue;
    if (typeof typeValue === 'object') return Object.values(typeValue)[0] || 0;
    return 0;
  }

  /**
   * Apply profit margin to convert cost price to sale price
   * Formula: (costPrice / (1 - profit)) * 1.08 (8% adjustment factor)
   * @param {object} params - Parameters object
   * @param {string} params.category - Component category
   * @param {string} params.type - Component type
   * @param {string} params.subtype - Optional subtype
   * @param {number} params.costPrice - Base cost price
   * @param {number} params.profit - Profit margin (0-1 range, e.g., 0.4 = 40%)
   * @returns {number} Sale price with profit applied
   */
  applyProfitMargin({ type, category, subtype, costPrice, profit }) {
    let baseProfit =
      typeof profit === 'number'
        ? profit
        : this.getDefaultProfit(category, type, subtype) || 0;

    return roundTo((costPrice / (1 - baseProfit)) * 1.08, 0);
  }

  /**
   * Register variables for use in formula and condition evaluation
   * Variables are merged into existing ones (not replaced)
   * @param {object} variables - Key-value pairs to use in formulas
   */
  setVariables(variables) {
    this.variables = { ...this.variables, ...variables };
  }

  calculateGST({ grandSale }) {
    try {
      const result = grandSale * (this.variables.gst ?? 0);
      // Kiểm tra kết quả
      if (result <= 0) {
        // throw new Error(`${GST} calculate: ${result}`);
        return 0;
      }

      return result;
    } catch (error) {
      console.error(error?.message);
      // Sentry.captureException(error);
    }
  }

  changeToUSD({ vnd }) {
    try {
      const exchangeRate = this.variables.exchangeRate || 1;
      return roundTo(vnd / exchangeRate, 2);
    } catch (error) {
      console.error(error?.message);
      // Sentry.captureException(error);
      return vnd;
    }
  }

  /**
   * Calculate cost for a single component with rules and caching
   * Applies default prices, conditional rules, and base formulas
   * Results cached to speed up repeated calculations
   * @param {object} params - Component parameters
   * @param {string} params.type - Component type (acrylic, hinges, etc.)
   * @param {string} params.category - Component category (doors, materials, etc.)
   * @param {string} params.subtype - Optional subtype (dulux, melamine, etc.)
   * @param {number} params.quantity - Component quantity
   * @param {number} params.unitPrice - Unit price (uses default if not provided)
   * @param {object} params.conditions - Extra variables for conditional evaluation
   * @returns {number} Calculated total cost for component
   */
  calculateComponentCost({
    type,
    category,
    subtype,
    quantity = 1,
    unitPrice,
    conditions = {},
    ...props
  }) {
    try {
      const cacheKey = this.getCacheKey(
        type,
        category,
        subtype,
        quantity,
        unitPrice
      );
      if (!conditions || Object.keys(conditions).length === 0) {
        const cached = this.cache.get(cacheKey);
        if (cached !== undefined && quantity && unitPrice) {
          return cached;
        }
      }

      let basePrice =
        unitPrice || this.getDefaultPrice(category, type, subtype) || 0;

      const rules = this.getRules(category, type, subtype);

      if (rules && rules.conditions && rules.conditions.length > 0) {
        basePrice = this.applyRules(basePrice, quantity, rules, conditions);
      }

      let result;
      if (rules && rules.baseFormula) {
        result = createSafeEvaluator(rules.baseFormula, {
          quantity,
          unitPrice: basePrice,
          ...this.variables,
          ...conditions,
          ...props,
        });
      } else {
        // result = basePrice * quantity;
        new Error(
          `${type}->${subtype ? subtype : ''} ${'Không tìm thấy baseFormula'}`
        );
      }

      // Kiểm tra kết quả
      if (result <= 0 && quantity !== 0) {
        throw new Error(
          `${type}->${subtype ? subtype : ''} ${quantity} calculate: ${result}`
        );
      }

      const componentCost = {
        cost: result,
        rule: rules?.baseFormula,
        basePrice: String(basePrice),
        variables: Object.fromEntries(
          Object.entries(this.variables ?? {}).map(([k, v]) => [k, String(v)])
        ),
      };

      if (!conditions || Object.keys(conditions).length === 0) {
        this.cache.set(cacheKey, componentCost);
      }

      return componentCost;
    } catch (error) {
      console.error(error?.message);
      // Sentry.captureException(error);
      return { cost: 0, rule: undefined };
    }
  }

  /**
   * Calculate cost for a single component with rules and caching
   * Applies default prices, conditional rules, and base formulas
   * Results cached to speed up repeated calculations
   * @param {object} params - Component parameters
   * @param {string} params.type - Component type (acrylic, hinges, etc.)
   * @param {string} params.category - Component category (doors, materials, etc.)
   * @param {string} params.subtype - Optional subtype (dulux, melamine, etc.)
   * @param {number} params.profit - Component progit
   * @param {object} params.conditions - Extra variables for conditional evaluation
   * @returns {number} Calculated total cost for component
   */
  calculateComponentSale({
    type,
    category,
    subtype,
    cost,
    profit,
    conditions = {},
  }) {
    try {
      const cacheKey = this.getCacheKey(type, category, subtype, cost);
      if (!conditions || Object.keys(conditions).length === 0) {
        const cached = this.cache.get(cacheKey);
        if (cached !== undefined) return cached;
      }

      let defaultProfit =
        profit || this.getDefaultProfit(category, type, subtype) || 0;

      const rules = this.getRules(category, type, subtype);

      let result;
      if (rules && rules.baseFormulaWithProfit) {
        result = createSafeEvaluator(rules.baseFormulaWithProfit, {
          cost,
          profit: defaultProfit,
          ...this.variables,
          ...conditions,
        });
      } else {
        // result = basePrice * quantity;
        new Error(
          `${type}->${subtype ? subtype : ''} ${'Không tìm thấy baseFormulaWithProfit'}`
        );
      }

      // Kiểm tra kết quả
      if (result <= 0 && cost !== 0) {
        throw new Error(
          `${type}->${subtype ? subtype : ''} cost: ${cost} -> sale: ${result}`
        );
      }

      const componentSale = {
        cost: result,
        rule: rules?.baseFormulaWithProfit,
        profit: String(profit),
      };

      if (!conditions || Object.keys(conditions).length === 0) {
        this.cache.set(cacheKey, componentSale);
      }

      return componentSale;
    } catch (error) {
      console.error(error?.message);
      // Sentry.captureException(error);
      return { cost: 0, rule: undefined };
    }
  }

  /**
   * Get pricing rules for component (supports nested structure)
   * @param {string} category - Category
   * @param {string} type - Component type
   * @param {string} subtype - Optional subtype (for nested rules)
   * @returns {object} Rules object
   */
  getRules(category, type, subtype = null) {
    const typeRules = this.pricingRules[category]?.[type];

    if (!typeRules) return null;

    // If subtype is provided and rules are nested
    if (
      subtype &&
      typeof typeRules === 'object' &&
      typeRules.baseFormula === undefined
    ) {
      return typeRules[subtype] || null;
    }

    // Return the rule if it's a rule object (has baseFormula)
    if (typeRules && typeRules.baseFormula) {
      return typeRules;
    }

    return null;
  }

  getDefaultPrice(category, type, subtype = null) {
    return this.getNestedValue(this.defaultPrices, category, type, subtype);
  }

  getDefaultProfit(category, type, subtype = null) {
    return this.getNestedValue(this.defaultProfits, category, type, subtype);
  }

  applyRules(basePrice, quantity, rules, conditions = {}) {
    let adjustedPrice = basePrice;
    const context = { quantity, basePrice, ...this.variables, ...conditions };

    if (rules.conditions && Array.isArray(rules.conditions)) {
      for (const condition of rules.conditions) {
        if (createSafeConditionEvaluator(condition.if, context)) {
          if (condition.apply === 'discount') {
            adjustedPrice *= 1 - condition.value;
          } else if (condition.apply === 'surcharge') {
            adjustedPrice *= 1 + condition.value;
          } else if (condition.apply === 'formula') {
            adjustedPrice = createSafeEvaluator(condition.value, context);
          } else {
            adjustedPrice = createSafeEvaluator(condition.apply, context);
          }
        }
      }
    }

    return adjustedPrice;
  }

  /**
   * Aggregate all costs from registry into category totals
   * Uses iterative summation for better performance than recursion
   * @param {object} registry - PricingRegistry data with nested categories
   * @returns {object} Totals: { doors, materials, component, accessory, labor, special, grand }
   */
  aggregateCosts(registry) {
    const totals = {
      doors: 0,
      materials: 0,
      component: 0,
      accessory: 0,
      labor: 0,
      special: 0,
      transport: 0,
      grand: 0,
    };

    for (const category of Object.keys(totals).slice(0, -1)) {
      if (registry[category]) {
        totals[category] = this.sumCategory(registry[category]);
      }
    }

    totals.grand = Object.values(totals)
      .slice(0, -1)
      .reduce((a, b) => a + b, 0);
    return totals;
  }

  /**
   * Recursively sum all numeric values in a category object
   * Uses iterative stack approach to avoid recursion depth issues
   * Skips rule definition objects (has baseFormula property)
   * @param {object} category - Category object with nested structure
   * @returns {number} Sum of all numeric values in category
   */
  sumCategory(category) {
    let total = 0;
    const stack = [category];

    while (stack.length > 0) {
      const current = stack.pop();

      for (const key in current) {
        const value = current[key];

        if (typeof value === 'number') {
          total += value;
        } else if (Array.isArray(value)) {
          total += value.reduce((sum, item) => {
            if (typeof item === 'number') return sum + item;
            if (typeof item === 'object' && item !== null && item.cost) {
              return sum + item.cost;
            }
            return sum;
          }, 0);
        } else if (
          typeof value === 'object' &&
          value !== null &&
          !value.baseFormula
        ) {
          stack.push(value);
        }
      }
    }

    return total;
  }

  /**
   * Get detailed breakdown of costs per subcategory
   * Shows item count and totals for array-based costs
   * @param {object} registry - PricingRegistry data
   * @returns {object} Breakdown: { category: { type: total or { items, total } } }
   */
  getDetailedBreakdown(registry) {
    const breakdown = {};

    for (const category in registry) {
      breakdown[category] = {};
      const categoryData = registry[category];

      for (const key in categoryData) {
        const value = categoryData[key];

        if (typeof value === 'number') {
          breakdown[category][key] = value;
        } else if (Array.isArray(value)) {
          breakdown[category][key] = {
            items: value.length,
            total: value.reduce((sum, item) => sum + (item.cost || 0), 0),
          };
        } else if (typeof value === 'object' && value !== null) {
          breakdown[category][key] = this.sumCategory(value);
        }
      }
    }

    return breakdown;
  }
}

export default PricingCalculator;
